import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import './WarpTransition.css';
import { lockBodyScroll } from '../../utils/bodyScrollLock';

const STREAKS = Array.from({ length: 24 }, (_, index) => index);
const GLYPHS = ['/', '0', '1', '9', '9', '//', 'R', 'N'];

const WarpTransition = ({ phase, label, variant, departureMs = 0 }) => {
  const releaseScrollLockRef = useRef(null);

  useEffect(() => {
    releaseScrollLockRef.current = lockBodyScroll();

    return () => {
      releaseScrollLockRef.current?.();
      releaseScrollLockRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (phase === 'arrival') {
      releaseScrollLockRef.current?.();
      releaseScrollLockRef.current = null;
    }
  }, [phase]);

  const status =
    phase === 'charge'
      ? 'CHARGING RELAY'
      : phase === 'jump'
      ? 'CROSSING SIGNAL'
      : 'DESTINATION LOCKED';

  return (
    <div
      className={`warpTransition warpTransition--${variant} warpTransition--${phase}${
        departureMs ? ' warpTransition--departure' : ''
      }`}
      style={{ '--warp-departure-ms': `${departureMs}ms` }}
      role="status"
      aria-live="assertive"
      aria-label={`${label}. ${status}`}
    >
      <div className="warpTransition__grid" aria-hidden="true" />
      <div className="warpTransition__vignette" aria-hidden="true" />

      <div className="warpTransition__tunnel" aria-hidden="true">
        {STREAKS.map((streak) => (
          <span
            key={streak}
            className="warpTransition__streak"
            style={{
              '--streak-angle': `${streak * 15}deg`,
              '--streak-delay': `${(streak % 8) * -0.06}s`,
              '--streak-length': `${95 + (streak % 5) * 32}px`,
            }}
          />
        ))}
      </div>

      <div className="warpTransition__reactor" aria-hidden="true">
        <span className="warpTransition__ring warpTransition__ring--outer" />
        <span className="warpTransition__ring warpTransition__ring--middle" />
        <span className="warpTransition__ring warpTransition__ring--inner" />
        <span className="warpTransition__core" />
      </div>

      <div className="warpTransition__readout">
        <span className="warpTransition__status">{status}</span>
        <h1 className="warpTransition__label">{label}</h1>
        <div className="warpTransition__meter" aria-hidden="true">
          <span />
        </div>
        <div className="warpTransition__glyphs" aria-hidden="true">
          {GLYPHS.map((glyph, index) => (
            <span key={`${glyph}-${index}`}>{glyph}</span>
          ))}
        </div>
      </div>

      <div className="warpTransition__flash" aria-hidden="true" />
      <div className="warpTransition__arrivalScan" aria-hidden="true" />
    </div>
  );
};

WarpTransition.propTypes = {
  phase: PropTypes.oneOf(['charge', 'jump', 'arrival']).isRequired,
  label: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['roll', 'danger', 'portal']).isRequired,
  departureMs: PropTypes.number,
};

export default WarpTransition;
